import { useMemo, useState } from 'react';
import { Check, Info, Tag, Users, X } from 'lucide-react';
import type { Segment } from '../../lib/supabase';
import { formatNumber } from '../../lib/format';
import type { MailUser } from '../../types';
import { GruppenFilter } from '../../components/GruppenFilter/GruppenFilter';
import {
  ARTEN, alterText, gruppenInfo, passt, type FilterWert, type GruppenArt, type GruppenInfo,
} from '../../components/GruppenFilter/gruppenInfo';
import { gruppenFuer, ortText, schulenVon, type Stand } from './modell';

type Props = {
  stand: Stand;
  user: MailUser | null;
  gruppen: Segment[];
  laedt: boolean;
  aendern: (teil: Partial<Stand>) => void;
  zeigeFehler: boolean;
};

const ART_REIHENFOLGE: GruppenArt[] = ['mitglieder', 'familien', 'nicht-zahlaktiv', 'tag', 'ehemalige', 'sonstige'];

export function SchrittEmpfaenger({ stand, user, gruppen, laedt, aendern, zeigeFehler }: Props) {
  // Nur Gruppen, die zur Sportschule der Mail passen (und die die Rolle sieht).
  const moeglich = useMemo(() => {
    const ids = new Set<string>();
    return gruppenFuer(stand, user, gruppen).flatMap((a) => a.gruppen).filter((g) => (ids.has(g.id) ? false : (ids.add(g.id), true)));
  }, [stand, user, gruppen]);
  const infos = useMemo(() => new Map(moeglich.map((g) => [g.id, gruppenInfo(g)])), [moeglich]);

  const schulen = schulenVon(stand).map((s) => s.name);
  const alleSchulen = schulen.length >= 7;
  const uebergreifendMoeglich = moeglich.some((g) => infos.get(g.id)!.schulen.length === 0);
  // Vorgefiltert auf die Sportschule der Mail; übergreifende Gruppen nur auf Wunsch
  // (bei „alle Sportschulen" sind sie das Naheliegende).
  const standard: FilterWert = useMemo(
    () => ({ suche: '', schulen: [], uebergreifend: alleSchulen || schulen.length === 0, arten: [], alter: null }),
    [alleSchulen, schulen.length],
  );
  const [filter, setFilter] = useState<FilterWert>(standard);
  const angeboteneArten = ART_REIHENFOLGE.filter((a) => moeglich.some((g) => infos.get(g.id)!.art === a));

  const gefiltert = moeglich.filter((g) => passt(g, filter, infos.get(g.id)));
  const ohneAlter = filter.alter ? moeglich.filter((g) => !infos.get(g.id)!.alter && passt(g, { ...filter, alter: null }, infos.get(g.id))).length : 0;

  const abschnitte = useMemo(() => {
    const reihen = (liste: Segment[]) => [...liste].sort((a, b) => {
      const ia = ART_REIHENFOLGE.indexOf(infos.get(a.id)!.art);
      const ib = ART_REIHENFOLGE.indexOf(infos.get(b.id)!.art);
      return ia - ib || a.name.localeCompare(b.name, 'de');
    });
    const out: { titel: string; gruppen: Segment[] }[] = schulen.map((s) => ({
      titel: s,
      gruppen: reihen(gefiltert.filter((g) => infos.get(g.id)!.schulen.includes(s))),
    }));
    out.push({ titel: 'Sportschulübergreifend', gruppen: reihen(gefiltert.filter((g) => infos.get(g.id)!.schulen.length === 0)) });
    return out.filter((a) => a.gruppen.length > 0);
  }, [gefiltert, infos, schulen]);

  const intern = gruppen.filter((g) => g.always_included);
  const gewaehlt = gruppen.filter((g) => stand.segmentIds.includes(g.id));
  const summe = gewaehlt.reduce((n, g) => n + (g.contact_count ?? 0), 0);

  const umschalten = (id: string) => {
    aendern({ segmentIds: stand.segmentIds.includes(id) ? stand.segmentIds.filter((x) => x !== id) : [...stand.segmentIds, id] });
  };

  if (laedt) return <div className="loading-spinner" style={{ margin: '2rem auto' }} role="status" aria-label="Gruppen werden geladen" />;

  return (
    <div data-tour="assistent-arbeit">
      {moeglich.length === 0 ? (
        <div className="alert alert-warning">
          <Info size={16} aria-hidden="true" />
          <div>Für {ortText(stand) || 'diese Auswahl'} gibt es noch keine Empfängergruppe. Beschreib unten, wen die Mail erreichen soll — die Redaktion legt die Gruppe an.</div>
        </div>
      ) : (
        <GruppenFilter
          id="empfaenger-filter"
          wert={filter}
          onChange={setFilter}
          standard={standard}
          schulen={schulen}
          mitUebergreifend={uebergreifendMoeglich}
          arten={angeboteneArten}
          treffer={gefiltert.length}
          gesamt={moeglich.length}
          ohneAlter={ohneAlter}
        />
      )}

      {gewaehlt.length > 0 && (
        <div className="as-gewaehlt" aria-label="Gewählte Gruppen">
          <span className="as-gewaehlt-label">Gewählt:</span>
          {gewaehlt.map((g) => (
            <span key={g.id} className="as-gewaehlt-chip">
              {g.name} <span className="as-gewaehlt-zahl">{formatNumber(g.contact_count)}</span>
              <button type="button" onClick={() => umschalten(g.id)} aria-label={`${g.name} abwählen`}><X size={13} aria-hidden="true" /></button>
            </span>
          ))}
        </div>
      )}

      {moeglich.length > 0 && gefiltert.length === 0 && (
        <p className="as-leer-hinweis">Keine Gruppe passt zu den Filtern. <button type="button" className="zf-aendern" onClick={() => setFilter(standard)}>Filter zurücksetzen</button></p>
      )}

      {abschnitte.map((a) => (
        <section key={a.titel} className="as-abschnitt">
          <h3 className="as-abschnitt-titel"><Users size={16} aria-hidden="true" /> {a.titel === 'Sportschulübergreifend' ? a.titel : `Gruppen für ${a.titel}`}</h3>
          {a.titel === 'Sportschulübergreifend' && (
            <p className="as-warum">Gruppen über alle Sportschulen. Nur nehmen, wenn die Mail wirklich alle betrifft.</p>
          )}
          <div className="as-gruppen">
            {a.gruppen.map((g) => <GruppeZeile key={g.id} g={g} info={infos.get(g.id)!} aktiv={stand.segmentIds.includes(g.id)} onUmschalten={() => umschalten(g.id)} />)}
          </div>
        </section>
      ))}

      <div className={`as-summe${gewaehlt.length === 0 ? ' as-summe--leer' : ''}`} aria-live="polite">
        {gewaehlt.length === 0
          ? 'Noch keine Gruppe gewählt.'
          : <>Gewählt: <strong>{gewaehlt.length} {gewaehlt.length === 1 ? 'Gruppe' : 'Gruppen'}</strong> mit bis zu <strong>{formatNumber(summe)} Postfächern</strong>. Doppelte zählen nur einmal; ohne Einwilligung bekommt niemand die Mail.</>}
      </div>
      {zeigeFehler && gewaehlt.length === 0 && <p className="field-error" role="alert">Bitte mindestens eine Gruppe wählen.</p>}

      {intern.length > 0 && (
        <div className="alert alert-info" style={{ marginTop: '1rem' }}>
          <Info size={16} aria-hidden="true" />
          <div>
            <strong>Immer in Kopie:</strong> {intern.map((g) => `${g.name} (${g.contact_count})`).join(' · ')}.
            {' '}So wissen Tresen, Studioleitungen und Redaktion, was rausging.
          </div>
        </div>
      )}

      <div className="form-group" style={{ marginTop: '1.25rem' }}>
        <label className="form-label" htmlFor="zielgruppe">Passt keine Gruppe genau? (freiwillig)</label>
        <textarea id="zielgruppe" className="form-textarea" rows={2} value={stand.zielgruppe}
          placeholder="z. B. Nur die Eltern der Kids-Gruppe am Dienstag (6–9 Jahre)."
          onChange={(e) => aendern({ zielgruppe: e.target.value })} />
        <p className="form-hint">Wähl die Gruppe, die am nächsten dran ist, und beschreib hier, wen die Mail erreichen soll. Die Redaktion passt die Empfänger vor der Freigabe an.</p>
      </div>
    </div>
  );
}

function GruppeZeile({ g, info, aktiv, onUmschalten }: { g: Segment; info: GruppenInfo; aktiv: boolean; onUmschalten: () => void }) {
  const leer = (g.contact_count ?? 0) === 0;
  return (
    <label className={`as-gruppe${aktiv ? ' aktiv' : ''}${leer ? ' leer' : ''}`}>
      <input type="checkbox" checked={aktiv} onChange={onUmschalten} disabled={leer && !aktiv} />
      <span className="as-gruppe-text">
        <strong>{g.name}</strong>
        <span className="as-gruppe-merkmale">
          {info.art === 'tag' ? <span className="badge badge-info"><Tag size={11} aria-hidden="true" /> CRM-Tag</span>
            : <span className="badge badge-muted">{ARTEN.find((x) => x.id === info.art)?.label}</span>}
          {info.alter && <span className="badge badge-muted">{alterText(info.alter)}</span>}
        </span>
        {g.description && <span>{g.description}</span>}
      </span>
      <span className="as-gruppe-zahl">{leer ? 'leer' : `${formatNumber(g.contact_count)} Postfächer`}</span>
      {aktiv && <Check size={15} className="as-gruppe-haken" aria-hidden="true" />}
    </label>
  );
}
